import React, { useEffect, useState, useCallback } from "react"
import Button from "@mui/material/Button"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"
import EditIcon from "@mui/icons-material/Edit"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import Paper from "@mui/material/Paper"
import Chip from "@mui/material/Chip"
import LinearProgress from "@mui/material/LinearProgress"
import Pagination from "@mui/material/Pagination"
import Switch from "@mui/material/Switch"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import { useSnackbar } from "notistack"
import { PERMISSIONS } from "constants/permission"
import {
  UsePermissionVerification,
  UseConfirmDialog,
} from "components/customHooks"
import { verifyResponse } from "utils/httpRequest"
import {
  EVENT_TYPE_LIST,
  EVENT_PLATFORM_LIST,
  EVENT_STATUS_LIST,
} from "constants/global"
import { getTierPrograms } from "services/tierProgram"
import {
  getAllEvents,
  deleteEvent,
} from "services/event"
import { formatEventDateForDisplay } from "utils/time"
import EventDialog from "./EventDlg"
import EventParticipantsDialog from "./EventParticipantsDlg"
import { StyledEventWrapper } from "./Event.style"

const Event = () => {
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState([])
  const [tierPrograms, setTierPrograms] = useState([])
  const [tierProgramsLoading, setTierProgramsLoading] = useState(true)
  const [tierProgramsError, setTierProgramsError] = useState(null)
  const [titleFilterVal, setTitleFilterVal] = useState("")
  const [debouncedTitle, setDebouncedTitle] = useState("")
  const [eventTypeFilterVal, setEventTypeFilterVal] = useState("all")
  const [tierProgramFilterVal, setTierProgramFilterVal] = useState("all")
  const [statusFilterVal, setStatusFilterVal] = useState("all")
  const [isFeaturedFilterVal, setIsFeaturedFilterVal] = useState("all")
  const [hidePastEvents, setHidePastEvents] = useState(true)
  const [hideInactiveEvents, setHideInactiveEvents] = useState(true)
  const [sortBy, setSortBy] = useState("startDate")
  const [sortOrder, setSortOrder] = useState("DESC")
  const [openEventDialog, setOpenEventDialog] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [participantsEvent, setParticipantsEvent] = useState(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [totalCount, setTotalCount] = useState(0)

  const AccessControl = UsePermissionVerification()
  const { enqueueSnackbar } = useSnackbar()

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Delete Confirm",
    description: "Are you sure you want to delete this event?",
    agreeText: "Yes",
    disagreeText: "No",
  })

  const clearFilters = () => {
    setEventTypeFilterVal("all")
    setTierProgramFilterVal("all")
    setStatusFilterVal("all")
    setIsFeaturedFilterVal("all")
    setHidePastEvents(true)
    setHideInactiveEvents(true)
    setTitleFilterVal("")
    setDebouncedTitle("")
    setSortBy("startDate")
    setSortOrder("DESC")
    setPage(1)
  }

  // Debounce search input so we don't re-query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedTitle(titleFilterVal)
    }, 400)
    return () => clearTimeout(t)
  }, [titleFilterVal])

  const refreshEvents = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAllEvents({
        page,
        limit,
        search: debouncedTitle || undefined,
        eventType: eventTypeFilterVal !== "all" ? eventTypeFilterVal : undefined,
        tierProgramId: tierProgramFilterVal !== "all" ? Number(tierProgramFilterVal) : undefined,
        status: statusFilterVal !== "all" ? Number(statusFilterVal) : undefined,
        isFeatured: isFeaturedFilterVal !== "all" ? (isFeaturedFilterVal === "true" ? 1 : 0) : undefined,
        isActive: hideInactiveEvents ? 1 : undefined,
        hidePastEvents: hidePastEvents ? 1 : undefined,
        sortBy,
        sortOrder,
      })

      verifyResponse(response.data)
      // Backend may return either {data:{events,total}} or directly {events,total}
      const payload = response?.data?.data ?? response?.data ?? {}
      setEvents(payload.events || [])
      setTotalCount(payload.total || 0)
    } catch (error) {
      console.error("Error fetching events:", error)
      enqueueSnackbar("Failed to load events", { variant: "error" })
    }
    setLoading(false)
  }, [page, limit, debouncedTitle, eventTypeFilterVal, tierProgramFilterVal, statusFilterVal, isFeaturedFilterVal, hideInactiveEvents, hidePastEvents, sortBy, sortOrder, enqueueSnackbar])

  // Load tier programs on component mount
  useEffect(() => {
    const loadTierPrograms = async () => {
      try {
        setTierProgramsLoading(true)
        setTierProgramsError(null)
        const response = await getTierPrograms()
        const programs = response.data || response
        if (Array.isArray(programs)) {
          setTierPrograms(programs)
        } else {
          throw new Error("Tier programs API returned non-array data")
        }
      } catch (error) {
        console.error("Failed to load tier programs:", error)
        setTierProgramsError(error.message)
        enqueueSnackbar("Failed to load tier programs", { variant: "error" })
      } finally {
        setTierProgramsLoading(false)
      }
    }
    
    loadTierPrograms()
  }, [enqueueSnackbar])

  const deleteEventById = async (eventId) => {
    setLoading(true)
    try {
      const response = await deleteEvent(eventId)
      verifyResponse(response.data)
      switch (response.data.rtnCode) {
        case 200:
          enqueueSnackbar("Event Deleted", { variant: "success" })
          refreshEvents()
          break
        default:
          enqueueSnackbar("Delete Event Failed", { variant: "warning" })
      }
    } catch (error) {
      enqueueSnackbar("Delete Event Failed", { variant: "error" })
    }
    setLoading(false)
  }

  const handleDeleteEvent = (eventId) => {
    openConfirmDialog(() => deleteEventById(eventId))
  }

  const handleEditEvent = (event) => {
    setSelectedEvent(event)
    setOpenEventDialog(true)
  }

  const handleCreateEvent = () => {
    setSelectedEvent(null)
    setOpenEventDialog(true)
  }

  const handleEventDialogClose = () => {
    setOpenEventDialog(false)
    setSelectedEvent(null)
  }

  const handleEventSubmit = () => {
    handleEventDialogClose()
    refreshEvents()
  }

  useEffect(() => {
    refreshEvents()
  }, [refreshEvents])

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return "default"
      case 1: return "success"
      case 2: return "error"
      case 3: return "warning"
      default: return "default"
    }
  }

  const getStatusText = (status) => {
    const statusItem = EVENT_STATUS_LIST.find(item => item.value === status)
    return statusItem ? statusItem.text : "Unknown"
  }

  const getEventTypeText = (eventType) => {
    const typeItem = EVENT_TYPE_LIST.find(item => item.value === eventType)
    return typeItem ? typeItem.text : eventType
  }

  const getTierAccessText = (event) => {
    const type = event?.tierProgram?.type || event?.tierAccess || "Free"
    return String(type)
  }

  const getPlatformText = (platform) => {
    const platformItem = EVENT_PLATFORM_LIST.find(item => item.value === platform)
    return platformItem ? platformItem.text : platform
  }

  const canCreateEvent = AccessControl.VerifyAccess([PERMISSIONS.EVENT.EVENT_CREATE])
  const canEditEvent = AccessControl.VerifyAccess([PERMISSIONS.EVENT.EVENT_UPDATE])
  const canDeleteEvent = AccessControl.VerifyAccess([PERMISSIONS.EVENT.EVENT_DELETE])
  const canViewParticipants = AccessControl.VerifyAccess([
    PERMISSIONS.EVENT.EVENT_REGISTRATION_LIST,
  ])
  const totalPages = Math.max(1, Math.ceil(totalCount / limit))

  return (
    <StyledEventWrapper>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Event Management</h1>
          <p className="page-subtitle">
            Manage webinars and live events, including access tiers, status, and
            scheduling.
          </p>
        </div>
        <div className="actions"></div>
      </PageHeading>

      <div className="event-surface">
        <div className="filter-shell">
          <div className="filter-grid">
            <TextField
              className="search-field"
              label="Search by title"
              value={titleFilterVal}
              onChange={(e) => {
                setTitleFilterVal(e.target.value)
                setPage(1)
              }}
              size="small"
            />
            <FormControl size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={eventTypeFilterVal}
                onChange={(e) => {
                  setEventTypeFilterVal(e.target.value)
                  setPage(1)
                }}
                label="Event Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {EVENT_TYPE_LIST.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Tier Access</InputLabel>
              <Select
                value={tierProgramFilterVal}
                onChange={(e) => {
                  setTierProgramFilterVal(e.target.value)
                  setPage(1)
                }}
                label="Tier Access"
                disabled={tierProgramsLoading || !!tierProgramsError}
              >
                <MenuItem value="all">All Tiers</MenuItem>
                {tierProgramsLoading ? (
                  <MenuItem disabled>Loading tiers...</MenuItem>
                ) : tierProgramsError ? (
                  <MenuItem disabled>Failed to load tiers</MenuItem>
                ) : (
                  tierPrograms.map((tier) => (
                    <MenuItem key={tier.id} value={tier.id}>
                      {tier.type.charAt(0).toUpperCase() + tier.type.slice(1)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilterVal}
                onChange={(e) => {
                  setStatusFilterVal(e.target.value)
                  setPage(1)
                }}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                {EVENT_STATUS_LIST.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              className="filter-switch"
              control={
                <Switch
                  checked={hidePastEvents}
                  onChange={(e) => {
                    setHidePastEvents(e.target.checked)
                    setPage(1)
                  }}
                  color="primary"
                />
              }
              label="Hide past events"
            />
            <FormControlLabel
              className="filter-switch"
              control={
                <Switch
                  checked={hideInactiveEvents}
                  onChange={(e) => {
                    setHideInactiveEvents(e.target.checked)
                    setPage(1)
                  }}
                  color="primary"
                />
              }
              label="Hide inactive events"
            />
            <FormControl size="small">
              <InputLabel>Featured</InputLabel>
              <Select
                value={isFeaturedFilterVal}
                onChange={(e) => {
                  setIsFeaturedFilterVal(e.target.value)
                  setPage(1)
                }}
                label="Featured"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Featured</MenuItem>
                <MenuItem value="false">Not Featured</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value)
                  setPage(1)
                }}
                label="Sort By"
              >
                <MenuItem value="startDate">Start Date</MenuItem>
                <MenuItem value="createdAt">Created</MenuItem>
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="currentParticipants">Participants</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value)
                  setPage(1)
                }}
                label="Order"
              >
                <MenuItem value="DESC">DESC</MenuItem>
                <MenuItem value="ASC">ASC</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="filter-actions">
            <div className="result-meta">Total events: {totalCount}</div>
            <div className="filter-action-right">
              <Button
                className="clear-btn"
                variant="outlined"
                size="medium"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
              <Button
                className="create-btn"
                variant="contained"
                size="medium"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={handleCreateEvent}
                disabled={!canCreateEvent}
              >
                Create Event
              </Button>
            </div>
          </div>
        </div>

        <div className="table-shell">
          <TableContainer component={Paper}>
            {loading && <LinearProgress />}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Tier Access</TableCell>
                  <TableCell>Timezone</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Participants</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Featured</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && events.length === 0 && (
                  <TableRow>
                    <TableCell className="table-empty" colSpan={12}>
                      No events found.
                    </TableCell>
                  </TableRow>
                )}
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <div className="event-title">{event.title}</div>
                      {event.shortDescription && (
                        <div className="event-subtitle">{event.shortDescription}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getEventTypeText(event.eventType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{event.platform && getPlatformText(event.platform)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getTierAccessText(event)}
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{event.timezone || "UTC"}</TableCell>
                    <TableCell>
                      {formatEventDateForDisplay(event.startDate, event.timezone)}
                    </TableCell>
                    <TableCell>
                      {formatEventDateForDisplay(event.endDate, event.timezone)}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          canViewParticipants
                            ? "View registered participants"
                            : "You do not have permission to view participants"
                        }
                      >
                        <span>
                          <Button
                            className="participant-count-btn"
                            onClick={() => setParticipantsEvent(event)}
                            disabled={!canViewParticipants}
                            size="small"
                          >
                            {event.currentParticipants || 0} / {event.maxParticipants || "∞"}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(event.status || 1)}
                        size="small"
                        color={getStatusColor(event.status || 1)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={event.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={event.isActive ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {event.isFeatured ? (
                        <VisibilityIcon color="primary" />
                      ) : (
                        <VisibilityOffIcon color="disabled" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="row-actions">
                        <Tooltip title="Edit Event">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleEditEvent(event)}
                              disabled={!canEditEvent}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete Event">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteEvent(event.id)}
                              disabled={!canDeleteEvent}
                              color="error"
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {totalPages > 1 && (
          <div className="pagination-wrap">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => {
                setPage(value)
              }}
              color="primary"
              shape="rounded"
              size="small"
            />
          </div>
        )}
      </div>

      <EventDialog
        open={openEventDialog}
        event={selectedEvent}
        onClose={handleEventDialogClose}
        onSubmit={handleEventSubmit}
      />

      <EventParticipantsDialog
        open={Boolean(participantsEvent)}
        event={participantsEvent}
        onClose={() => setParticipantsEvent(null)}
      />

      {ConfirmDialog}
    </StyledEventWrapper>
  )
}

export default Event
